import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useTranslation from 'next-translate/useTranslation';
import { UserForm, userValidation } from '@zydalabs/storefront-components';
import { Button } from '@zydalabs/zac-react';
import { StyledPageWrapper } from 'common/styles';
import { useUserInfo, useUpdateUser } from 'service/hooks';
import { changeRoute, localizeErrorMessages } from 'common/utils';
import { URLS } from 'common/constants';
import { LogoutButton } from 'modules/userModule/components';

import { StyledUserProfileWrapper } from './style';

const StoreFrontHeaderComponent = dynamic(() => import('common/components/StoreFrontHeader'));

const UserProfile: React.FC = () => {
  const { t } = useTranslation('userProfile');
  const { data: userInfo, isLoading: isUserInfoLoading } = useUserInfo();
  const updateUser = useUpdateUser();

  const userFields = {
    fullName: t('fullName'),
    email: `${t('emailField')} ${t('optional')}`,
  };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userValidation),
  });

  useEffect(() => {
    if (userInfo) {
      setValue('fullName', userInfo.name);
      setValue('email', userInfo.email || '');
    }
  }, [userInfo]);

  const handleOnSubmit = async data => {
    await updateUser({ name: data.fullName, email: data.email });
    changeRoute(URLS.HOME);
  };

  const Logout = <LogoutButton size="small" variant="tertiary" text={t('common:logout')} />;

  return (
    <StyledPageWrapper>
      <StoreFrontHeaderComponent title={t('profile')} additionalElement={Logout} />
      <StyledUserProfileWrapper>
        <UserForm fields={userFields} errors={localizeErrorMessages(t, userFields, errors)} control={control} />
        <Button
          type="submit"
          onClick={handleSubmit(handleOnSubmit)}
          isDisabled={isUserInfoLoading}
          isLoading={isUserInfoLoading}
          isFluid
          text={t('submit')}
        />
      </StyledUserProfileWrapper>
    </StyledPageWrapper>
  );
};

export default UserProfile;
